<template>
  <div v-if="readonly">
    <div class="d-flex align-center mb-2">
      <p class="ma-0 pa-0 subtitle-2 blue-grey--text text--darken-1">
        {{ labelText }}
      </p>
      <v-btn
        v-if="inputItems.length > 30"
        icon
        x-small
        color="blue-grey darken-1"
        class="ms-2"
        :ripple="false"
        @click="showChips = !showChips"
      >
        <v-icon size="16"> {{ readonlyIcon }}</v-icon>
      </v-btn>
    </div>
    <v-slide-y-transition>
      <div v-show="showChips">
        <div class="d-flex flex-wrap">
          <ChipAvatar
            v-for="user in inputItems"
            backgroundColor="blue-grey lighten-3"
            borderColor="blue-grey lighten-2"
            class="me-1 mb-1"
            disablePopover
            small
            :account="user"
            :key="user.id"
            :vuetifyProps="{
              ripple: false,
            }"
          />
        </div>
      </div>
    </v-slide-y-transition>
  </div>
  <!-- Do not use property cache-items in this autocomplete-->
  <!-- it has some conflicts with no-filter prop-->
  <!-- https://github.com/vuetifyjs/vuetify/issues/11365 -->
  <v-autocomplete
    v-else
    chips
    dense
    outlined
    no-filter
    return-object
    hide-details
    append-icon="mdi-chevron-down"
    item-value="id"
    item-text="title"
    data-testid="assigneeSelector"
    data-tourid="filters-assignee"
    class="caption assignee-selector-filter white"
    ref="autocomplete"
    id="assignees-selector-filter"
    :loading="isLoading"
    :menu-props="{
      allowOverflow: true, // This prop allow us to be sure of displaying the list of options below the autocomplete. When this props is not passed to v-menu, it auto-position itself if the list of elements is too large, overlaping the area where user is typing the search criteria.
      contentClass: 'autocomplete-menu', // The 'auto' prop invalidates the 'max-height' prop we could pass to v-menu. We have to pass a custom classname as a workaround. https://github.com/vuetifyjs/vuetify/issues/11144#issuecomment-859358889
      nudgeBottom: 2,
    }"
    :multiple="multiple"
    :hide-selected="!isSearching"
    :disabled="disabled"
    :items="inputItems"
    :label="labelText"
    :rules="validationRules || rules"
    :search-input.sync="searchTerm"
    :value="selectedAccountIds"
    @change="handleChange"
  >
    <template v-slot:prepend-item>
      <v-card
        elevation="0"
        class="cursor-pointer pl-3 mt-n2 blue--text"
        :loading="isLoading"
        @click="handleClearSelection"
      >
        <p class="pt-3 caption font-weight-500">
          {{ multiple ? $t('common.clear_all') : $t('common.clear') }}
        </p>
      </v-card>
      <v-text-field
        hide-details
        single-line
        clearable
        prepend-icon="search"
        class="d-flex align-center search-inside-filter pt-0 mt-0 px-4 mb-1"
        :value="searchTerm"
        @input="searchTerm = $event"
      >
        <template v-slot:label>
          <span class="body3">{{ $t('common.search') }}</span>
        </template>
      </v-text-field>
    </template>
    <template v-slot:prepend-inner>
      <slot />
    </template>
    <template v-if="showEmptyState" v-slot:no-data>
      <EmptyState
        data-testid="assigneeSelector-emptyState"
        :icon="emptyStateIcon"
        :title="emptyStateTitle"
        :message="emptyStateMessage"
      />
    </template>
    <template v-slot:selection="data">
      <span
        v-if="multiple"
        class="mr-n3 mb-1"
        :class="{ 'd-none': data.index > 2 }"
      >
        <ChipAvatar
          v-if="data.index < 2"
          v-bind="data.attrs"
          xsmall
          :account="data.item"
          :vuetifyProps="{
            inputValue: data.selected,
            ripple: false,
          }"
          borderColor="transparent"
          backgroundColor="transparent"
          disablePopover
          @click="data.select"
          @click:close="handleRemove(data.item)"
        />
        <v-tooltip v-if="data.index === 2" bottom max-width="400px">
          <template v-slot:activator="{ on }">
            <v-chip v-on="on" small color="blue-grey lighten-3">
              + {{ selectedAccounts.length - 2 }}
            </v-chip>
          </template>
          <ul class="ma-0 pa-0 ms-2">
            <div v-for="option in selectedAccounts" :key="option.value">
              <li class="ma-0 pa-0">
                {{ getShortenedName(option) }}
              </li>
            </div>
          </ul>
        </v-tooltip>
      </span>
      <div v-else class="d-flex single-selection">
        <UserAvatar
          class="single-selection-avatar"
          size="24px"
          fontSize="10px"
          :account="data.item"
        />
        <span class="account-single-name text-truncate ml-2">
          {{ getShortenedName(data.item) }}
        </span>
      </div>
    </template>
    <template v-slot:item="data">
      <div class="ml-0 d-flex">
        <v-list-item-avatar class="d-flex justify-center mr-2 my-1 ml-n2">
          <v-avatar v-if="data.item.isTeam" color="teal lighten-5">
            <v-icon color="teal"> mdi-account-group </v-icon>
          </v-avatar>
          <UserAvatar v-else size="24px" fontSize="10px" :account="data.item" />
        </v-list-item-avatar>

        <v-list-item-content>
          <div class="d-flex justify-space-between align-center">
            <v-list-item-title>
              <text-highlight
                highlightClass="yellow lighten-1"
                data-private
                highlightComponent="span"
                class="blue-grey--text text--darken-3 font-size-13 text-truncate"
                :queries="searchTerm || ''"
              >
                {{ data.item.title }}
              </text-highlight>
            </v-list-item-title>
            <v-chip v-if="data.item.isTeam" x-small>
              {{ $t('common.team_one') }}
            </v-chip>
          </div>
        </v-list-item-content>
      </div>
    </template>
  </v-autocomplete>
</template>

<script>
import assigneesSelectorMixin from './AssigneesSelectorMixin.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'

import ChipAvatar from '@/components/ChipAvatar.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import EmptyState from '@/components/EmptyState.vue'

export default {
  name: 'AssigneesSelectorFilter',

  mixins: [assigneesSelectorMixin, trackingMixin],

  components: {
    UserAvatar,
    ChipAvatar,
    EmptyState,
  },

  props: {
    readonly: Boolean,
    allowClear: Boolean,
  },

  data() {
    return {
      menuStateWatcher: null,
      showChips: true,
    }
  },

  watch: {
    '$refs.autocomplete'(autocompleteRef) {
      if (!autocompleteRef || !this.multiple || !this.emitAutoApplyMetadata) {
        return
      }

      this.setupAssigneeMenuStateWatcher()
    },
  },

  mounted() {
    this.setupAssigneeMenuStateWatcher()
  },

  beforeDestroy() {
    this.menuStateWatcher?.()
  },

  computed: {
    readonlyIcon() {
      return this.showChips ? 'mdi-chevron-up' : 'mdi-chevron-down'
    },
  },

  methods: {
    getClearSelectionValue() {
      return this.multiple ? [] : null
    },

    emitLegacyClearSelection(value) {
      this.$emit('clear', value)
    },

    emitInstantClearSelection(value) {
      this.$emit(
        'clear',
        this.buildAssigneeChangePayload(value, {
          commitMode: 'instant',
          source: 'user',
          shouldApply: true,
        })
      )
    },

    emitDeferredClearSelection(value) {
      if (!this.menuInteractionId) this.startDeferredAssigneeInteraction()

      this.menuCurrentValue = value
      this.$emit(
        'clear',
        this.buildAssigneeChangePayload(value, {
          commitMode: 'deferredAfterBlur',
          source: 'user',
          shouldApply: false,
          interactionId: this.menuInteractionId,
        })
      )
    },

    shouldDeferClearSelection() {
      return this.emitAutoApplyMetadata && this.multiple
    },

    handleClearSelection() {
      const value = this.getClearSelectionValue()

      if (!this.emitAutoApplyMetadata) {
        this.emitLegacyClearSelection(value)
        return
      }

      if (!this.shouldDeferClearSelection()) {
        this.emitInstantClearSelection(value)
        return
      }

      this.emitDeferredClearSelection(value)
    },

    canWatchAssigneeMenuState() {
      return (
        this.multiple &&
        this.emitAutoApplyMetadata &&
        !!this.$refs?.autocomplete
      )
    },

    handleWatchedAssigneeMenuState(isOpen, wasOpen) {
      if (isOpen === wasOpen) return

      this.handleAssigneeMenuStateChange(isOpen)
    },

    setupAssigneeMenuStateWatcher() {
      if (!this.canWatchAssigneeMenuState() || this.menuStateWatcher) return

      this.menuStateWatcher = this.$refs.autocomplete.$watch(
        'isMenuActive',
        (isOpen, wasOpen) =>
          this.handleWatchedAssigneeMenuState(isOpen, wasOpen)
      )
    },
  },
}
</script>

<style scoped lang="scss">
.assignee-selector-filter {
  min-width: 200px !important;
}
</style>

<style lang="scss">
.assignee-selector-filter {
  .v-input__append-outer {
    background-color: $color-blue-grey-lighten3;
    margin: 0 !important;
    height: 32px;
    display: flex;
    align-items: center;
  }

  input#assignees-selector-filter {
    caret-color: transparent;
    color: transparent;
  }

  .autocomplete-menu {
    max-height: 250px !important;
  }

  .v-input__control {
    .v-input__slot {
      background-color: $color-white;
      height: 32px !important;
      min-height: 32px !important;

      fieldset legend {
        width: fit-content !important;
      }

      .v-select__slot {
        display: flex;
        align-items: center;
        .v-label {
          top: 8px;
          background: $color-white !important;
          padding: 0 4px;
          font-size: $font-size-12 !important;
          line-height: 16px;
          letter-spacing: 0.4px !important;
          div {
            text-overflow: ellipsis;
            overflow: hidden;
          }
        }
        .v-label--active {
          top: 10px;
        }
        .v-select__selections {
          padding-top: 10px !important;
          .single-selection {
            .single-selection-avatar {
              margin-top: -3px;
            }
            max-width: inherit;
          }
          .v-chip__content {
            font-size: $font-size-12 !important;
            line-height: 16px;
            letter-spacing: 0.4px !important;
          }
        }
        .v-select__selections:has(span) {
          input {
            height: 0;
            padding: 0;
            width: 10px !important;
            min-width: 10px !important;
          }
        }
      }
      .v-input__append-inner {
        margin-top: 4px !important;
      }
    }
  }

  .selection-item-container {
    max-width: 180px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .clear-button:hover {
    opacity: 0.7;
  }

  .search-inside-filter .v-input__control .v-input__slot::before,
  .search-inside-filter .v-input__control .v-input__slot::after {
    border-style: hidden;
  }
}
</style>
